import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { reportAxiosError } from '@shared/providers/src/HoneybadgerProvider'
import api, { Service } from '@shared/services'
import { AuthUtils, formatErrorToString, getErrorCode, includesOneOfErrorMessages, Logger, timeoutCode } from '@shared/utils'

import Error from '@pages/Misc/Error'
import NotAuthorized from '@pages/Misc/NotAuthorized'
import NotFound from '@pages/Misc/NotFound'

const log = Logger('AxiosErrorHandler')

// Module-level refs to access React state/functions from the interceptor
let navigateRef = null
let setErrorRef = null

// Register interceptor at module load time to ensure it's ready before any components render
const interceptorId = api.interceptors.response.use(
  (response) => response,
  (error) => {
    const honeybadgerPromise = reportAxiosError(error)

    if (error.config?.skipHandling) throw error

    const status = error.response?.status
    const shouldShowError =
      error.code === timeoutCode ||
      status === 404 ||
      status >= 500 ||
      (status === 403 && includesOneOfErrorMessages(error, ['does not has permissions on this endpoint']))

    if (shouldShowError) {
      setErrorRef?.(error)
      honeybadgerPromise?.then((url) => {
        if (url) {
          error.honeybadgerUrl = url
          setErrorRef?.((prev) => (prev === error ? Object.create(error) : prev))
        }
      })
    }

    // Handle 401 authentication errors
    if (status === 401 && includesOneOfErrorMessages(error, ['You are not authorized', 'User not authenticated'])) {
      log.info('User logged out: ', formatErrorToString(error))
      Service.abortAndReinitialize('Operation canceled due to logout.')
      AuthUtils.logout()

      navigateRef?.('/login')
    }

    throw error
  }
)

export default function AxiosErrorHandler({ children }) {
  const navigate = useNavigate()
  const [error, setError] = useState(undefined)
  const errorCode = useMemo(() => getErrorCode(error), [error])

  // Update module-level refs when navigate or setError changes
  useEffect(() => {
    navigateRef = navigate
    setErrorRef = setError
  }, [navigate])

  if (errorCode === 401) {
    return <NotAuthorized error={error} resetErrorBoundary={() => setError(undefined)} />
  }

  if (errorCode === 404) {
    return <NotFound error={error} resetErrorBoundary={() => setError(undefined)} />
  }

  if (error) {
    return <Error code={errorCode} error={error} resetErrorBoundary={() => setError(undefined)} />
  }

  return children
}
